import { useState, useEffect, useCallback, useRef } from 'react';
import { boardsApi } from '../api';
import type { Board, CreateBoardInput, UpdateBoardInput } from '../types';

export function useBoards() {
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBoards = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await boardsApi.list();
      setBoards(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch boards');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  const createBoard = async (input: CreateBoardInput) => {
    const board = await boardsApi.create(input);
    setBoards((prev) => [...prev, board]);
    return board;
  };

  const joinBoard = async (inviteCode: string) => {
    const board = await boardsApi.join(inviteCode);
    setBoards((prev) => {
      if (prev.find((b) => b.id === board.id)) {
        return prev;
      }
      return [...prev, board];
    });
    return board;
  };

  return {
    boards,
    isLoading,
    error,
    fetchBoards,
    createBoard,
    joinBoard,
  };
}

export function useBoard(id: string) {
  const [board, setBoard] = useState<Board | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const fetchBoard = useCallback(async () => {
    if (!id) return;
    // Only show loading spinner on initial load (stale-while-revalidate pattern)
    // This prevents the page from blinking when refetching data
    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await boardsApi.get(id);
      setBoard(data);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch board');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const updateBoard = async (input: UpdateBoardInput) => {
    const updated = await boardsApi.update(id, input);
    setBoard(updated);
    return updated;
  };

  const lockBoard = async () => {
    const updated = await boardsApi.lock(id);
    setBoard(updated);
    return updated;
  };

  const unlockBoard = async () => {
    const updated = await boardsApi.unlock(id);
    setBoard(updated);
    return updated;
  };

  const randomizeNumbers = async () => {
    const updated = await boardsApi.randomizeNumbers(id);
    setBoard(updated);
    return updated;
  };

  const publishBoard = async () => {
    const updated = await boardsApi.publish(id);
    setBoard(updated);
    return updated;
  };

  const unpublishBoard = async () => {
    const updated = await boardsApi.unpublish(id);
    setBoard(updated);
    return updated;
  };

  const completeBoard = async () => {
    const updated = await boardsApi.complete(id);
    setBoard(updated);
    return updated;
  };

  const inviteParticipant = async (email: string) => {
    await boardsApi.inviteParticipant(id, email);
  };

  return {
    board,
    isLoading,
    error,
    fetchBoard,
    refetch: fetchBoard,
    updateBoard,
    lockBoard,
    unlockBoard,
    randomizeNumbers,
    publishBoard,
    unpublishBoard,
    completeBoard,
    inviteParticipant,
  };
}
